/**
 * A macOS desktop, drawn.
 *
 * Shared by both Mac practicals, as the Linux art is shared by both of
 * its own - they are standing on the same machine and it would be odd
 * if it looked like two.
 *
 * Drawn rather than screenshotted, for the reason all of these are: a
 * screenshot dates the moment Apple changes the wallpaper. The shapes
 * are what have to be right, and on a Mac the shapes are genuinely
 * different from the other two: one menu bar along the top that belongs
 * to whichever program you are using rather than to the window, and a
 * Dock along the bottom with the Trash at the end of it.
 */

const SIDEBAR = ['AirDrop', 'Recents', 'Applications', 'Desktop', 'Documents', 'Downloads'];

const FILES = [
  { icon: 'doc', name: 'homework.pages', date: '12 Sep', size: '18 KB' },
  { icon: 'img', name: 'birthday.jpg', date: '09 Sep', size: '2.1 MB' },
  { icon: 'folder', name: 'School project', date: '04 Sep', size: '--' },
  { icon: 'pdf', name: 'reading list.pdf', date: '01 Sep', size: '340 KB' },
  { icon: 'doc', name: 'story draft.pages', date: '28 Aug', size: '24 KB' },
];

function FileGlyph({ kind, x, y }) {
  if (kind === 'folder') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <path className="mac-g-folder" d="M0,0.6 h1.8 l0.5,0.7 h2.4 v3.1 h-4.7 z" />
      </g>
    );
  }
  if (kind === 'img') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="mac-g-img" x="0" y="0.6" width="4.6" height="3.8" rx="0.6" />
        <circle className="mac-g-img-sun" cx="1.4" cy="1.8" r="0.5" />
        <path className="mac-g-img-hill" d="M0.3,4.2 L1.8,2.5 L2.9,3.5 L3.8,2.8 L4.3,4.2 Z" />
      </g>
    );
  }
  const tone = kind === 'pdf' ? 'mac-g-pdf' : 'mac-g-doc';
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className={tone} d="M0.4,0.5 h2.7 l1.3,1.3 v2.7 h-4 z" />
      <path className="mac-g-fold" d="M3.1,0.5 v1.3 h1.3 z" />
    </g>
  );
}

/** One program in the Dock. Rounded squares, as Big Sur onwards. */
function DockIcon({ kind, x, running }) {
  return (
    <g transform={`translate(${x} 88)`}>
      {kind === 'finder' && (
        <>
          <rect className="mac-g-tile is-finder" x="0" y="0" width="9" height="9" rx="2" />
          <rect className="mac-g-finder-left" x="0" y="0" width="4.5" height="9" rx="2" />
          <circle className="mac-g-finder-eye" cx="3" cy="3.4" r="0.45" />
          <circle className="mac-g-finder-eye" cx="6" cy="3.4" r="0.45" />
          <path className="mac-g-finder-smile" d="M2.6,5.8 q1.9,1.4 3.8,0" />
        </>
      )}
      {kind === 'safari' && (
        <>
          <rect className="mac-g-tile is-safari" x="0" y="0" width="9" height="9" rx="2" />
          <circle className="mac-g-safari-ring" cx="4.5" cy="4.5" r="3" />
          <path className="mac-g-safari-needle" d="M6.2,2.8 L4,4.3 L2.8,6.2 L5,4.7 Z" />
        </>
      )}
      {kind === 'mail' && (
        <>
          <rect className="mac-g-tile is-mail" x="0" y="0" width="9" height="9" rx="2" />
          <rect className="mac-g-mail-body" x="1.6" y="2.8" width="5.8" height="3.6" rx="0.6" />
          <path className="mac-g-mail-flap" d="M1.6,3.2 L4.5,5.2 L7.4,3.2" />
        </>
      )}
      {kind === 'launchpad' && (
        <>
          <rect className="mac-g-tile is-launchpad" x="0" y="0" width="9" height="9" rx="2" />
          {[2.6, 4.5, 6.4].map((cy) =>
            [2.6, 4.5, 6.4].map((cx) => (
              <circle key={`${cx}-${cy}`} className="mac-g-pad-dot" cx={cx} cy={cy} r="0.62" />
            )),
          )}
        </>
      )}
      {kind === 'term' && (
        <>
          <rect className="mac-g-tile is-term" x="0" y="0" width="9" height="9" rx="2" />
          <rect className="mac-g-term-bar" x="0" y="0" width="9" height="1.8" rx="2" />
          <text className="mac-g-term-text" x="1.8" y="6.6">{'>_'}</text>
        </>
      )}
      {kind === 'trash' && (
        <>
          <path className="mac-g-trash" d="M2.2,2.6 h4.6 l-0.6,6.2 h-3.4 z" />
          <rect className="mac-g-trash" x="1.5" y="1.5" width="6" height="0.9" rx="0.45" />
          <rect className="mac-g-trash" x="3.6" y="0.7" width="1.8" height="0.8" rx="0.4" />
        </>
      )}
      {/* A running program gets a dot under it. On a Mac that dot is
          the only thing saying an app is open at all. */}
      {running && <circle className="mac-run-dot" cx="4.5" cy="10.4" r="0.5" />}
    </g>
  );
}

/**
 * `showFinder` draws a Finder window over the wallpaper; `running` adds
 * Terminal to the Dock with the dot under it, which is exactly what a
 * Mac does when you launch something that was not pinned there.
 */
export default function MacDesktopArt({ showFinder = false, running = false }) {
  return (
    <svg viewBox="0 0 160 100" aria-hidden="true">
      <defs>
        <linearGradient id="mac-wall" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#123b63" />
          <stop offset="45%" stopColor="#1d6584" />
          <stop offset="100%" stopColor="#0d2b45" />
        </linearGradient>
        <radialGradient id="mac-bloom" cx="0.5" cy="0.42" r="0.55">
          <stop offset="0%" stopColor="#6fd5e0" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#6fd5e0" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="160" height="100" fill="url(#mac-wall)" />
      <ellipse cx="80" cy="42" rx="78" ry="46" fill="url(#mac-bloom)" />

      {/* ---------- menu bar ---------- */}
      <rect className="mac-menubar" x="0" y="0" width="160" height="5" />

      {/* the apple */}
      <g>
        <path className="mac-logo" d="M4.5,1.6 q1,-0.5 1.9,0.2 q0.9,-0.7 1.9,-0.2 q0.9,0.5 0.6,1.6 q-0.3,1.2 -1.2,1.6 q-0.6,0.3 -1.3,0 q-0.7,0.3 -1.3,0 q-0.9,-0.4 -1.2,-1.6 q-0.3,-1.1 0.6,-1.6 z" />
        <path className="mac-logo" d="M6.5,1.1 q0.1,-0.7 0.9,-0.9 q0.1,0.7 -0.9,0.9 z" />
      </g>

      {/* the app menus. They belong to the program, not the window,
          which is the single biggest difference a Windows user meets */}
      <text className="mac-menu-text is-app" x="11.5" y="3.5">Finder</text>
      <text className="mac-menu-text" x="20.5" y="3.5">File</text>
      <text className="mac-menu-text" x="26.5" y="3.5">Edit</text>
      <text className="mac-menu-text" x="32.5" y="3.5">View</text>
      <text className="mac-menu-text" x="39.5" y="3.5">Go</text>
      <text className="mac-menu-text" x="44.5" y="3.5">Window</text>
      <text className="mac-menu-text" x="54.5" y="3.5">Help</text>

      {/* status menus, right to left: battery, wifi, spotlight,
          control centre, clock */}
      <rect className="mac-batt" x="105" y="1.7" width="5" height="2.2" rx="0.6" />
      <rect className="mac-batt-fill" x="105.4" y="2.1" width="3.2" height="1.4" rx="0.3" />
      <rect className="mac-batt-cap" x="110.3" y="2.3" width="0.6" height="1" rx="0.3" />
      <path className="mac-wifi" d="M113,3.4 a2.6,2.6 0 0 1 3.6,0 M114.1,4.3 a1.2,1.2 0 0 1 1.4,0" />
      <circle className="mac-spot-glass" cx="120.4" cy="2.6" r="1.1" />
      <line className="mac-spot-handle" x1="121.2" y1="3.4" x2="122" y2="4.2" />
      <rect className="mac-cc-pill" x="126" y="1.6" width="2.2" height="2.4" rx="1.1" />
      <rect className="mac-cc-pill" x="128.8" y="1.6" width="2.2" height="2.4" rx="1.1" />
      <text className="mac-clock" x="157" y="3.5">Mon 14:32</text>

      {/* ---------- Finder ---------- */}
      {showFinder && (
        <g>
          <rect className="mac-win" x="40" y="12" width="104" height="68" rx="2.4" />

          {/* title bar, with the traffic lights at the left */}
          <rect className="mac-titlebar" x="40" y="12" width="104" height="7" rx="2.4" />
          <rect className="mac-titlebar" x="40" y="17" width="104" height="2" />
          <circle className="mac-light is-red" cx="44" cy="15.5" r="1.1" />
          <circle className="mac-light is-amber" cx="47.6" cy="15.5" r="1.1" />
          <circle className="mac-light is-green" cx="51.2" cy="15.5" r="1.1" />
          <text className="mac-win-title" x="92" y="16.4">Documents</text>

          {/* sidebar */}
          <rect className="mac-side" x="40" y="19" width="28" height="61" />
          <text className="mac-side-head" x="42.6" y="23">Favourites</text>
          {SIDEBAR.map((item, i) => (
            <g key={item}>
              {item === 'Documents' && (
                <rect className="mac-side-sel" x="41" y={24.8 + i * 6} width="26" height="5" rx="1.2" />
              )}
              <path className="mac-g-folder-small" d={`M43.4,${26 + i * 6} h1.4 l0.4,0.5 h1.9 v2.1 h-3.7 z`} />
              <text className={`mac-side-text ${item === 'Documents' ? 'is-sel' : ''}`} x="48.6" y={28.6 + i * 6}>
                {item}
              </text>
            </g>
          ))}

          {/* column headings */}
          <rect className="mac-cols" x="68" y="19" width="76" height="5" />
          <text className="mac-col-text" x="73" y="22.7">Name</text>
          <text className="mac-col-text" x="110" y="22.7">Date Modified</text>
          <text className="mac-col-text" x="134" y="22.7">Size</text>

          {/* files */}
          {FILES.map((f, i) => {
            const y = 24.6 + i * 7;
            return (
              <g key={f.name}>
                {i === 0 && <rect className="mac-row-sel" x="68.6" y={y + 0.4} width="74.8" height="6.2" rx="1.2" />}
                <FileGlyph kind={f.icon} x={70.4} y={y + 0.8} />
                <text className="mac-file-name" x="76.4" y={y + 4.6}>{f.name}</text>
                <text className="mac-file-meta" x="110" y={y + 4.6}>{f.date}</text>
                <text className="mac-file-meta" x="134" y={y + 4.6}>{f.size}</text>
              </g>
            );
          })}
        </g>
      )}

      {/* ---------- Dock ---------- */}
      <rect className="mac-dock" x="41" y="87" width="78" height="11.4" rx="3" />
      <DockIcon kind="finder" x={43} />
      <DockIcon kind="safari" x={54} />
      <DockIcon kind="mail" x={65} />
      <DockIcon kind="launchpad" x={76} />
      {/* Terminal is not in a Mac's Dock until you open one - which is
          exactly why the practical makes you find it with Spotlight. */}
      {running && <DockIcon kind="term" x={87} running />}
      <line className="mac-dock-rule" x1="100.5" y1="88.6" x2="100.5" y2="97" />
      <DockIcon kind="trash" x={104} />
    </svg>
  );
}
